// The replay's pure arithmetic (R528, R542): how a `Replay` frame reads.
//
// A `Replay` frame carries what the server's log holds for one window: the
// hulls, one sampled track per hull, and every damage record inside the
// window. Nothing here paints. Every function is a function of that frame
// and a time, so `replay.test.ts` reads them with no canvas.
//
// The replay film (`fast/film.ts`, R531) is the one reader. It plays the
// frame on the live WebGL2 engine, and it takes the fit, the pose, the run
// of a round, the sinkings and the pace from here, so the film states no
// rule of its own.
//
// The card of R528 — a panel of two 2D canvases over the map — was the
// other reader. The owner picked the film on 2026-09-05 and the card
// retired with it (R542); what it alone needed went with it.
//
// The figures ride data/presentation/presentation.toml
// [presentation.replay] (R312).

import {
  DamageKind, type Replay, type ReplayHit, type ReplayTrack,
} from '../gen/proto/aquaphobia';
import { mpxToPx } from '../world';
import {
  REPLAY_MARGIN_FRAC, REPLAY_MIN_WINDOW_PX, REPLAY_PLAY_RATE_X,
} from '../gen/presentation';
import { clamp01 } from '../render/math';

/** A world rectangle. */
export interface Bounds { x0: number; y0: number; x1: number; y1: number }

/** The square window the film is fitted to, in world px. */
export interface Fit { cx: number; cy: number; half: number }

/** Where one hull is, and which way it points, at one time. */
export interface Pose {
  x: number; y: number; hull: number; shield: number; heading: number;
}

/** How far `t` is from `t0` to `t1`, 0 to 1. A span of nothing is safe. */
function progress(t: number, t0: number, t1: number): number {
  return clamp01((t - t0) / Math.max(1e-6, t1 - t0));
}

/**
 * Playback speed: replay deciseconds consumed per wall millisecond.
 *
 * A replay clock is the MAP clock, and a world runs the map faster than
 * wall time (`-tacticalscale`, numbers.md §19). So a replay is paid at the
 * map scale as well as at the rate the player asked for (R534): a fight
 * the commander watched at x30 plays back at x30 times the rate, and the
 * rate label then means what it says.
 */
export function playRateDsPerMs(mapScale: number): number {
  const s = Number.isFinite(mapScale) && mapScale > 0 ? mapScale : 1;
  return (REPLAY_PLAY_RATE_X * s * 10) / 1000;
}

/** The damage kinds that something fired: a gun or a round. */
const FIRED_KINDS: readonly DamageKind[] = [
  DamageKind.DAMAGE_KIND_GUN, DamageKind.DAMAGE_KIND_GUN_SPLASH,
  DamageKind.DAMAGE_KIND_TORPEDO, DamageKind.DAMAGE_KIND_TORPEDO_SPLASH,
];

/**
 * Did a gun or a round deal this damage? Only then does the record carry a
 * source point. The seaquake, the settlement and the hull a ship pays
 * itself have no source, and their source point is (0, 0), which is the
 * corner of the map and not a place in the fight.
 */
export function isFiredDamage(kind: DamageKind): boolean {
  return FIRED_KINDS.includes(kind);
}

/**
 * The window the film is fitted to: every track point and every hit point
 * of the frame (the source point of a fired damage only), never tighter than `REPLAY_MIN_WINDOW_PX` of world, with
 * `REPLAY_MARGIN_FRAC` of margin around it.
 *
 * The window is square. The canvas scales it by one factor on both axes, so
 * the terrain under the fight keeps the shape the map draws.
 */
export function fitOf(r: Replay): Fit {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const eat = (xm: number, ym: number) => {
    const x = mpxToPx(xm), y = mpxToPx(ym);
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
  };
  for (const tr of r.tracks) for (const p of tr.points) eat(p.xMpx, p.yMpx);
  for (const h of r.hits) {
    eat(h.vxMpx, h.vyMpx);
    if (isFiredDamage(h.kind)) eat(h.sxMpx, h.syMpx);
  }
  if (!Number.isFinite(x0)) { x0 = 0; y0 = 0; x1 = 0; y1 = 0; }
  const half = Math.max((x1 - x0) / 2, (y1 - y0) / 2, REPLAY_MIN_WINDOW_PX / 2)
    * (1 + REPLAY_MARGIN_FRAC);
  return { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, half };
}

/** The heading of the segment that ends at point `i`, or the last one
 *  before it that moved. A hull that never moved points east. */
function headingAt(track: ReplayTrack, i: number): number {
  for (let k = Math.min(i, track.points.length - 1); k > 0; k--) {
    const a = track.points[k - 1], b = track.points[k];
    const dx = b.xMpx - a.xMpx, dy = b.yMpx - a.yMpx;
    if (dx !== 0 || dy !== 0) return Math.atan2(dy, dx);
  }
  return 0;
}

/**
 * Where a hull is at `tDs`, in world px.
 *
 * The position is the linear interpolation of the two samples around the
 * time, which is why the log samples an engaged hull at a steady cadence: a
 * hull must cross the water, never jump over it.
 *
 * Before its first point a hull is not on film and the function answers
 * null. After its last point it HOLDS that point: the log stopped sampling
 * it, which is not a statement that it left.
 */
export function poseAt(track: ReplayTrack, tDs: number): Pose | null {
  const pts = track.points;
  if (pts.length === 0 || tDs < pts[0].tDs) return null;
  let i = 0;
  while (i + 1 < pts.length && pts[i + 1].tDs <= tDs) i++;
  const a = pts[i], b = pts[i + 1];
  if (!b) {
    return {
      x: mpxToPx(a.xMpx), y: mpxToPx(a.yMpx), hull: a.hullPct, shield: a.shieldPct,
      heading: headingAt(track, i),
    };
  }
  const k = progress(tDs, a.tDs, b.tDs);
  return {
    x: mpxToPx(a.xMpx + (b.xMpx - a.xMpx) * k),
    y: mpxToPx(a.yMpx + (b.yMpx - a.yMpx) * k),
    hull: a.hullPct + (b.hullPct - a.hullPct) * k,
    shield: a.shieldPct + (b.shieldPct - a.shieldPct) * k,
    heading: headingAt(track, i + 1),
  };
}

/**
 * Where a round is at `tDs`, in world px: the straight run from the launch
 * point at `launch_t_ds` to the victim point at `t1_ds`. Null before the
 * launch and after the hit, when the round is not in the water.
 */
export function torpedoAt(hit: ReplayHit, tDs: number): { x: number; y: number } | null {
  if (tDs < hit.launchTDs || tDs > hit.t1Ds) return null;
  const k = progress(tDs, hit.launchTDs, hit.t1Ds);
  return {
    x: mpxToPx(hit.sxMpx + (hit.vxMpx - hit.sxMpx) * k),
    y: mpxToPx(hit.syMpx + (hit.vyMpx - hit.syMpx) * k),
  };
}

/**
 * When each hull sank, by hull index. A hull that is not in the answer
 * survived the window.
 *
 * The frame STATES a sinking per track (R534), and that statement wins:
 * the log holds a record of every hull that went down, whether or not the
 * damage that sank it is in this window. The old inference — the first hit
 * that took a hull to zero, or a last track point at zero hull — stays as
 * the fallback for a frame that carries no statement.
 */
export function sinkTimes(r: Replay): Map<number, number> {
  const out = new Map<number, number>();
  const put = (hull: number, tDs: number) => {
    const had = out.get(hull);
    if (had === undefined || tDs < had) out.set(hull, tDs);
  };
  for (const h of r.hits) {
    if (h.hullAfterX10 <= 0 && h.hullBeforeX10 > 0) put(h.victim, h.t1Ds);
  }
  for (const tr of r.tracks) {
    const last = tr.points[tr.points.length - 1];
    if (last && last.hullPct <= 0) put(tr.hull, last.tDs);
  }
  for (const tr of r.tracks) {
    if (tr.sunk) out.set(tr.hull, tr.sunkTDs);
  }
  return out;
}

/**
 * When each hull broke off a window battle, by hull index (R1451). A hull
 * whose shield reached zero leaves the arena at that second, so the film
 * fades it out there, as it fades a sunk hull, but with no blast and no
 * wreck. Only a window film states a withdrawal; a map film answers an
 * empty map.
 */
export function withdrawTimes(r: Replay): Map<number, number> {
  const out = new Map<number, number>();
  for (const tr of r.tracks) {
    if (tr.withdrawn) out.set(tr.hull, tr.withdrawnTDs);
  }
  return out;
}
